import type { TFunction } from "i18next";
import { useU2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useState, useRef, useEffect } from "react";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { AxiosInstance } from "axios";
import { useU2104LoadRolesHandlers } from "./U2104UseLoadRolesHandlers";
import { doLoadRolesAffiliation, type U2104Affiliation, type U2104AffiliationRole, type U2104Role } from "../../../../U2104Service";

// Gestion des handlers pour les actions d'une unité logique
export interface U2104AffilaitionRoleHandler {
  onAdd: () => void;
  onDelete: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104AffiliationRole>>;
  dataChangedEvent: number;
  onCheckRole: (role: U2104Role) => void;
  roles: U2104Role[];
}

// Paramètres pour les handlers des actions d'une unité logique
export interface U2104AffilaitionRoleHandlerParam {
  u2000AxiosAuthenticated: AxiosInstance;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104AffiliationRole>>;
  t: TFunction;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104Affiliation>>;
  organizationId: string;
}

// Gestion des handlers pour les actions d'une unité logique
export interface U2104AffilaitionRoleHandlerReturn {
  onAdd: () => void;
  onDelete: () => void;

  onDlgClose: (result: string) => void;
  isDialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;

  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U2104Affiliation>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104AffiliationRole>>;
  dataChangedEvent: number;

  onCheckRole: (role: U2104Role) => void;
  roles: U2104Role[];
}

// Implémentation des handlers pour les actions d'une unité logique
export function u2104AffilaitionRoleHandler(params: U2104AffilaitionRoleHandlerParam): U2104AffilaitionRoleHandlerReturn {
  const { u2000AxiosAuthenticated, gridStateHandlers, formStateHandlers, organizationId } = params;
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [roles, setRoles] = useState<U2104Role[]>([]);
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [localEditMode, _setLocalEditMode] = useState<U2000FormStateEnumEditMode>(U2000FormStateEnumEditMode.NONE);
  const localEditModeRef = useRef(localEditMode);

  useEffect(() => {
    localEditModeRef.current = localEditMode;
  }, [localEditMode]);

  // handlers pour le chargement des rôles
  const loadRolesHandlers = useU2104LoadRolesHandlers<U2104Role>({
    doLoadRolesFn: () => {
      return doLoadRolesAffiliation(u2000AxiosAuthenticated, organizationId);
    },
    onSuccess: (roles: U2104Role[]) => {
      updateRoleFromSelectedRow(roles);
    },
    onError: ({ error }) => {
      // Gérer les erreurs lors du chargement des rôles
      console.error("Error loading roles:", error);
    },
  });

  // Gestion du check/uncheck d'un rôle
  const onCheckRole = (role: U2104Role): void => {
    const updatedRoles = roles.map((r) => (r.id === role.id ? { ...r, idChecked: !r.idChecked } : r));
    setRoles(updatedRoles);
    const newData = formStateHandlers.getData();
    newData.roles = updatedRoles;
    formStateHandlers.setData(newData);
  };

  // Gestion de l'ajout d'une action d'unité logique
  const onAdd = (): void => {
    if (roles.length === 0) {
      loadRolesHandlers.loadRoles();
    } else {
      updateRoleFromSelectedRow(roles);
    }
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    setDialogOpen(true);
  };

  // Gestion de la suppression d'une action d'unité logique
  const onDelete = (): void => {
    setDeleteDialogOpen(true);
    return;
  };

  // Gestion de la fermeture du dialog de modification/ajout
  const onDlgClose = (result: string): void => {
    if (result === "cancel") {
      gridStateHandlers.setErrors([]);
      formStateHandlers.cancelData();
      setDialogOpen(false);
    } else if (result === "save") {
      if (formStateHandlers.getData().roles.length !== 0) {
        formStateHandlers.saveData();
        if (formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD) {
          const newData = formStateHandlers.getData().roles.filter((role) => role.idChecked);
          // Ajouter le nouvel objet au gridStateHandlers
          gridStateHandlers.setData(newData);
          gridStateHandlers.setTotalRows(newData.length);
          setDataChangedEvent(dataChangedEvent + 1);
        }
      }
    }
    setDialogOpen(false);
    return;
  };

  // Gestion de la fermeture du dialog de suppression
  const onDlgDeleteClose = (result: string): void => {
    if (result === "yes") {
      const selectedRows = gridStateHandlers.getSelectedRows();
      const remainingRows = gridStateHandlers.getData().filter((row) => !selectedRows.some((selected) => selected.id === row.id));
      gridStateHandlers.setData(remainingRows);

      gridStateHandlers.setTotalRows(remainingRows.length);
      setDataChangedEvent(dataChangedEvent + 1);
    }
    setDeleteDialogOpen(false);
    return;
  };

  return {
    onAdd,
    onDelete,
    onDlgClose,
    isDialogOpen,
    setDialogOpen,
    gridStateHandlers,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    dataChangedEvent,
    onCheckRole,
    roles,
    formStateHandlers,
  };

  // Met à jour la liste des rôles en fonction des lignes sélectionnées dans le gridStateHandlers
  function updateRoleFromSelectedRow(roles: U2104Role[]): void {
    const gridRoles = gridStateHandlers.getData();

    const newRoles: U2104Role[] = roles.map((role) => {
      const isSelected = gridRoles.some((gridRole) => gridRole.id === role.id);
      return {
        ...role,
        idChecked: isSelected,
      };
    });

    setRoles(newRoles);
  }
}
